import { useEffect } from "react";

const DEFAULT_TITLE = "Ubay - Daily Islamic Quizzes | Test Your Quranic Knowledge";
const DEFAULT_DESCRIPTION =
  "Ubay.pk is a free non-profit Islamic quiz platform in Pakistan. Answer one Quranic question every day, receive the correct answer next day, and win gifts through ballot.";
const DEFAULT_URL = "https://www.ubay.pk/";

/**
 * Hook to update document <title> and key SEO meta tags for each page.
 * Restores defaults on unmount so the home page always has the right meta.
 */
const usePageMeta = (title, description, canonicalPath = "/") => {
  useEffect(() => {
    const fullTitle = title || DEFAULT_TITLE;
    const fullDescription = description || DEFAULT_DESCRIPTION;
    const canonicalUrl = `https://www.ubay.pk${canonicalPath}`;

    // Title
    document.title = fullTitle;

    // Helper to set a meta attribute value; creates the element if absent
    const setMeta = (selector, attr, value, createAttrs) => {
      let el = document.querySelector(selector);
      if (!el && createAttrs) {
        el = document.createElement("meta");
        Object.entries(createAttrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="title"]', "content", fullTitle, { name: "title" });
    setMeta('meta[name="description"]', "content", fullDescription, { name: "description" });
    setMeta('meta[property="og:title"]', "content", fullTitle, { property: "og:title" });
    setMeta('meta[property="og:description"]', "content", fullDescription, { property: "og:description" });
    setMeta('meta[property="og:url"]', "content", canonicalUrl, { property: "og:url" });
    setMeta('meta[name="twitter:title"]', "content", fullTitle, { name: "twitter:title" });
    setMeta('meta[name="twitter:description"]', "content", fullDescription, { name: "twitter:description" });
    setMeta('meta[name="twitter:url"]', "content", canonicalUrl, { name: "twitter:url" });

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", canonicalUrl);

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta('meta[name="title"]', "content", DEFAULT_TITLE);
      setMeta('meta[name="description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:title"]', "content", DEFAULT_TITLE);
      setMeta('meta[property="og:description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:url"]', "content", DEFAULT_URL);
      setMeta('meta[name="twitter:title"]', "content", DEFAULT_TITLE);
      setMeta('meta[name="twitter:description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[name="twitter:url"]', "content", DEFAULT_URL);
      if (canonical) canonical.setAttribute("href", DEFAULT_URL);
    };
  }, [title, description, canonicalPath]);
};

export default usePageMeta;
